import Loading from 'components/common/Loading';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Cookie from 'services/Cookie';
import { useMe } from 'store/app/auth/action';
import authUser from 'store/selectors/authUser';
import autologout from 'utils/autologout';
import { getActiveAuthToken } from 'utils/getActiveAuthHeaders';
import redirect from 'utils/redirect';
import connector from './connector';

const AuthGate = ({ actions, authToken, keepSessionOpen, component: Component }) => {
  useEffect(() => {
    const cancelWorker = autologout(
      authToken,
      keepSessionOpen,
      actions.auth.tokenRefreshed,
      actions.visibility.setActive,
    );
    return () => {
      if (cancelWorker) {
        cancelWorker();
      }
    };
  }, [authToken, actions.auth, keepSessionOpen, actions.visibility.setActive]);

  const token = Cookie.get('token');
  const hasSession = useSelector(state => Boolean(getActiveAuthToken(state.auth)?.hash));
  const {
    isLoading: isLoadingMe,
    isUninitialized: isUninitializedMe,
    needsCompanyRefetch,
    isError: isMeError,
  } = useMe();
  const user = useSelector(authUser);
  const isGuest = isEmpty(token) || isEmpty(user);
  // No cookie means /me will never run (shouldFetchMe), so isUninitialized stays true forever.
  // That is a settled guest, not an in-flight session.
  const mePending =
    hasSession && (isLoadingMe || isUninitializedMe || needsCompanyRefetch || isMeError);

  useEffect(() => {
    // Do not bounce to login while a company refetch is still resolving the session; otherwise a
    // transient empty-user render during a switch would log the user out.
    if (!mePending && isGuest) {
      // Capture the protected URL only at the unauthenticated-redirect moment; login consumes it
      // once. Never persist it (a stale cross-company returnUrl redirects logins to the wrong tenant).
      redirect(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
    }
  }, [isGuest, mePending]);

  if (mePending) {
    return <Loading />;
  }

  if (isGuest) {
    return null;
  }

  return <Component />;
};

AuthGate.propTypes = {
  actions: PropTypes.object.isRequired,
  keepSessionOpen: PropTypes.bool.isRequired,
  authToken: PropTypes.object.isRequired,
  component: PropTypes.elementType.isRequired,
};

export default connector(AuthGate);
